import { DocumentPdf } from "@carbon/icons-react";
import { Button, ComboButton, MenuItem, MenuItemDivider } from "@carbon/react";
import { useModal } from "@flaminem/ui-components";
import React from "react";

import { AuthorizedItem } from "src/authentication/AuthorizedMenu.tsx";
import { Scope, ScopeResource } from "src/authentication/PermissionsProvider.tsx";
import { usePermissions } from "src/authentication/usePermissions";
import MenuButton from "src/components/MenuButton/MenuButton.tsx";
import { ModalTransition } from "src/components/Modal";
import { useMediaQuery, BREAKPOINT_LARGE } from "src/hooks/useMediaQuery.ts";
import { useT } from "src/internationalization";
import { AssignAlertModal } from "src/pages/AlertDetails/assign-alert/AssignAlertModal.tsx";
import { ReopenAlertModal } from "src/pages/AlertDetails/reopen-alert/ReopenAlertModal.tsx";
import { ValidateAlertModal } from "src/pages/AlertDetails/validate-alert/ValidateAlertModal/ValidateAlertModal";
import { Alert } from "src/types/alert.ts";

type ValidateAlertModalButtonsProps = {
  alert: Alert;
  case?: {
    id: string;
    typeCode: string;
  };
  onExportPdf?: () => void;
};

const ValidateAlertModalButtons: React.FC<ValidateAlertModalButtonsProps> = ({
  alert,
  case: caseData,
  onExportPdf,
}) => {
  const t = useT();
  const matches = useMediaQuery(BREAKPOINT_LARGE);
  const { hasPermissions } = usePermissions();

  const [confirmModal, assignModal, reopenModal] = [useModal(), useModal(), useModal()];

  const canShowActions = alert?.isAssigned && !alert?.isAlertValidated && alert.id;
  const canShowReopenAction = alert?.status === "closed" && alert.id;

  const renderAssignMenuItem = () => (
    <AuthorizedItem
      requiredPermissions={[{ resource: ScopeResource.ALERT, scopes: [Scope.ASSIGN] }]}
    >
      <MenuItemDivider />
      <MenuItem label={t("general.actions.assign")} onClick={assignModal.openModal} />
    </AuthorizedItem>
  );

  const renderActionButtons = () => {
    return hasPermissions([{ resource: ScopeResource.ALERT, scopes: [Scope.VALIDATE] }]) ? (
      <ComboButton
        label={t("page.alert.button.confirm")}
        onClick={confirmModal.openModal}
        tooltipAlignment="top-right"
      >
        {!alert?.isAlertValidated && renderAssignMenuItem()}

        {
          <MenuItem
            label={t("page.alert.button.exportPdf")}
            renderIcon={DocumentPdf}
            onClick={onExportPdf}
          />
        }
      </ComboButton>
    ) : (
      <Button kind="tertiary" renderIcon={DocumentPdf} onClick={onExportPdf}>
        {t("page.alert.button.exportPdf")}
      </Button>
    );
  };

  const renderReopenButton = () => {
    return hasPermissions([{ resource: ScopeResource.ALERT, scopes: [Scope.REOPEN] }]) ? (
      <ComboButton
        label={t("reopen.alert.button.label")}
        onClick={reopenModal.openModal}
        tooltipAlignment="top-right"
      >
        <MenuItem
          label={t("page.alert.button.exportPdf")}
          renderIcon={DocumentPdf}
          onClick={onExportPdf}
        />
      </ComboButton>
    ) : (
      <Button kind="tertiary" renderIcon={DocumentPdf} onClick={onExportPdf}>
        {t("page.alert.button.exportPdf")}
      </Button>
    );
  };

  const renderActions = () => {
    if (canShowActions) {
      if (!matches) {
        return (
          <MenuButton label={t("general.actions.all")} menuAlignment="bottom-end">
            <AuthorizedItem
              requiredPermissions={[{ resource: ScopeResource.ALERT, scopes: [Scope.VALIDATE] }]}
            >
              <MenuItem label={t("page.alert.button.confirm")} onClick={confirmModal.openModal} />
            </AuthorizedItem>

            {!alert.isAlertValidated && renderAssignMenuItem()}

            <>
              <MenuItemDivider />
              <MenuItem
                label={t("page.alert.button.exportPdf")}
                renderIcon={DocumentPdf}
                onClick={onExportPdf}
              />
            </>
          </MenuButton>
        );
      }

      return renderActionButtons();
    }

    if (canShowReopenAction) {
      return renderReopenButton();
    }

    return (
      <Button kind="tertiary" renderIcon={DocumentPdf} onClick={onExportPdf}>
        {t("page.alert.button.exportPdf")}
      </Button>
    );
  };

  return (
    <>
      <div className="flex">{renderActions()}</div>
      <ModalTransition>
        {assignModal.isModalOpen && (
          <AssignAlertModal
            alert={alert}
            onSubmit={assignModal.closeModal}
            onClose={assignModal.closeModal}
          />
        )}
        {confirmModal.isModalOpen && (
          <ValidateAlertModal
            onClose={confirmModal.closeModal}
            alert={alert}
            caseId={caseData?.id ?? ""}
          />
        )}
        {reopenModal.isModalOpen && (
          <ReopenAlertModal alert={alert} onClose={reopenModal.closeModal} />
        )}
      </ModalTransition>
    </>
  );
};

export default ValidateAlertModalButtons;
